import React, { useState } from 'react';
import {
  Box, Typography, TextField, Button, Grid, Chip, FormControl,
  InputLabel, Select, MenuItem, Autocomplete, Alert,
} from '@mui/material';

const BasicInfoStep = ({
  formData,
  setFormData,
  onSubmit,
  submitting,
  isCompleted,
}) => {
  const [errors, setErrors] = useState({});

  // Predefined options
  const degreeOptions = [
    'MBBS', 'MD', 'MS', 'DM', 'MCh', 'PhD', 'MSc', 'BSc', 'Diploma',
    'Fellowship', 'Certification', 'License'
  ];

  const specialtyOptions = [
    'Psychiatry', 'Clinical Psychology', 'Counseling Psychology',
    'Child Psychiatry', 'Addiction Psychiatry', 'Forensic Psychiatry',
    'Geriatric Psychiatry', 'Psychotherapy', 'Cognitive Behavioral Therapy',
    'Family Therapy', 'Group Therapy', 'Art Therapy', 'Music Therapy'
  ];

  const languageOptions = [
    { code: 'en', name: 'English' },
    { code: 'hi', name: 'Hindi' },
    { code: 'bn', name: 'Bengali' },
    { code: 'te', name: 'Telugu' },
    { code: 'mr', name: 'Marathi' },
    { code: 'ta', name: 'Tamil' },
    { code: 'gu', name: 'Gujarati' },
    { code: 'kn', name: 'Kannada' },
    { code: 'ml', name: 'Malayalam' },
    { code: 'pa', name: 'Punjabi' },
    { code: 'as', name: 'Assamese' },
    { code: 'or', name: 'Odia' },
  ];

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.full_name?.trim()) {
      newErrors.full_name = 'Full name is required';
    }

    if (!formData.degrees?.length) {
      newErrors.degrees = 'At least one degree is required';
    }

    if (!formData.registration_number?.trim()) {
      newErrors.registration_number = 'Registration number is required';
    } else if (!/^[A-Z0-9]{5,20}$/.test(formData.registration_number)) {
      newErrors.registration_number = 'Registration number must be 5-20 characters, alphanumeric only';
    }

    if (!formData.specialties?.length) {
      newErrors.specialties = 'At least one specialty is required';
    }

    if (!formData.languages?.length) {
      newErrors.languages = 'At least one language is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        Basic Information
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={4}>
        Tell us about your professional background and qualifications
      </Typography>

      {isCompleted && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Basic information has been completed successfully!
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Full Name */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Full Name"
            value={formData.full_name || ''}
            onChange={(e) => handleInputChange('full_name', e.target.value)}
            error={!!errors.full_name}
            helperText={errors.full_name}
            disabled={isCompleted}
          />
        </Grid>

        {/* Registration Number */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Registration Number (MCI/RCI)"
            value={formData.registration_number || ''}
            onChange={(e) => handleInputChange('registration_number', e.target.value.toUpperCase())}
            error={!!errors.registration_number}
            helperText={errors.registration_number || 'Enter your MCI or RCI registration number'}
            disabled={isCompleted}
          />
        </Grid>

        {/* Degrees */}
        <Grid item xs={12}>
          <Autocomplete
            multiple
            options={degreeOptions}
            value={formData.degrees || []}
            onChange={(_, newValue) => handleInputChange('degrees', newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Degrees & Qualifications"
                error={!!errors.degrees}
                helperText={errors.degrees || 'Select all your degrees and qualifications'}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option}
                  {...getTagProps({ index })}
                  color="primary"
                  variant="outlined"
                />
              ))
            }
            disabled={isCompleted}
          />
        </Grid>

        {/* Specialties */}
        <Grid item xs={12}>
          <Autocomplete
            multiple
            options={specialtyOptions}
            value={formData.specialties || []}
            onChange={(_, newValue) => handleInputChange('specialties', newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Specialties"
                error={!!errors.specialties}
                helperText={errors.specialties || 'Select your areas of specialization'}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option}
                  {...getTagProps({ index })}
                  color="secondary"
                  variant="outlined"
                />
              ))
            }
            disabled={isCompleted}
          />
        </Grid>

        {/* Languages */}
        <Grid item xs={12}>
          <Autocomplete
            multiple
            options={languageOptions}
            getOptionLabel={(option) => option.name}
            value={formData.languages || []}
            onChange={(_, newValue) => handleInputChange('languages', newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Languages Spoken"
                error={!!errors.languages}
                helperText={errors.languages || 'Select languages you can communicate in'}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option.name}
                  {...getTagProps({ index })}
                  color="info"
                  variant="outlined"
                />
              ))
            }
            disabled={isCompleted}
          />
        </Grid>
      </Grid>

      {!isCompleted && (
        <Box mt={4} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? 'Saving...' : 'Save & Continue'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default BasicInfoStep;
